import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import type {
  BankOfChangesMulti,
  BankOfChangesOption,
  BankOfChangesValue,
  CrossCheckClause,
  CrossCheckInsight,
  PastAgreementOption,
} from '../types/crossCheck';
import { formatClauseNumberForDisplay } from '../types/crossCheck';
import { PLAYBOOK_CONTEXT_MAX_CHARS } from './playbookContext';

export type AskAgentDomain = 'construction-loan' | 'real-estate';

/** What to include in Ask Agent context when both BoC and past negotiations exist on the card. */
export type AskAgentContextSource = 'bank_of_changes' | 'past_negotiations' | 'both';

export const ASK_AGENT_CONTEXT_SOURCE_QUESTION =
  'על מה אתה רוצה שאבסס את התשובה?\n\n' +
  '• בנק השינויים\n' +
  '• ארכיון המשא ומתן של הפרויקט\n' +
  '• שניהם';

export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_BANK = 'בנק השינויים';
export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_ARCHIVE = 'ארכיון המשא ומתן';
export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_BOTH = 'שניהם';

export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_BANK =
  'מעולה — אבסס את התשובה על בנק השינויים ובקשת הלקוח.';

export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_ARCHIVE =
  'מעולה — אבסס את התשובה על התאמות משא ומתן קודם ועל ארכיון המשא ומתן של הפרויקט.';

export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_BOTH =
  'מעולה — אבסס את התשובה על בנק השינויים, משא ומתן קודם, וארכיון הפרויקט.';

export const ASK_AGENT_CONTEXT_SOURCE_UNRECOGNIZED =
  'לא זיהיתי את הבחירה. בחר/י: בנק השינויים, ארכיון המשא ומתן של הפרויקט, או שניהם — או השתמש/י בכפתורים למטה.';

export const ASK_AGENT_INITIAL_QUESTION =
  'בהתבסס על ארכיון המשא ומתן של הפרויקט, מה ההמלצה שלך — לקבל את בקשת הלקוח כפי שהיא, לדחות, או להציע עריכה? אנא הסבר בקצרה.';

export const ASK_AGENT_INITIAL_QUESTION_MCC =
  'בהתבסס על התאמת הסעיף בהסכם המכר, בנק השינויים, סעיפים קיימים רלוונטיים, וארכיון המשא ומתן של הפרויקט — מה ההמלצה שלך לגבי בקשת הלקוח (לקבל, לדחות, או להציע עריכה)? אנא הסבר בקצרה.';

/** Shown immediately when the clause has no matched past negotiations. */
export const ASK_AGENT_NO_MATCHES_NOTICE =
  'לא נמצאו משא ומתן קודם שתואמים ישירות לסעיף זה.\n\n' +
  'האם תרצה שאבצע חיפוש מעמיק בכל הארכיון (תשובות מאת הצד שלנו במשא ומתן בחוזי העבר) כדי לאתר מקורות עקיפים שיכולים לסייע בהחלטה כיצד להגיב לבקשת הלקוח?';

export const ASK_AGENT_NO_MATCHES_NOTICE_MCC =
  'לא נמצאו התאמות ישירות לסעיף זה (הסכם מכר / בנק שינויים / סעיפים קיימים).\n\n' +
  'האם תרצה שאבצע חיפוש מעמיק בארכיון המשא ומתן של הפרויקט כדי לאתר מקורות עקיפים שיכולים לסייע בהחלטה כיצד להגיב לבקשת הלקוח?';

export const ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE =
  'לא נמצאו משא ומתן קודם לסעיף זה, ואין רשומות בארכיון של הפרויקט. לא ניתן לבצע חיפוש מעמיק.';

export const ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE_MCC =
  'לא נמצאו התאמות ישירות לסעיף זה, ואין רשומות בארכיון של הפרויקט. לא ניתן לבצע חיפוש מעמיק.';

export function getAskAgentInitialQuestion(domain: AskAgentDomain): string {
  return domain === 'construction-loan' ? ASK_AGENT_INITIAL_QUESTION : ASK_AGENT_INITIAL_QUESTION_MCC;
}

export function getAskAgentNoMatchesNotice(domain: AskAgentDomain): string {
  return domain === 'construction-loan' ? ASK_AGENT_NO_MATCHES_NOTICE : ASK_AGENT_NO_MATCHES_NOTICE_MCC;
}

export function getAskAgentNoMatchesNoPlaybookNotice(domain: AskAgentDomain): string {
  return domain === 'construction-loan'
    ? ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE
    : ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE_MCC;
}

export const ASK_AGENT_DEEP_RESEARCH_QUESTION =
  'בצע חיפוש מעמיק בכל הארכיון. על בסיס מקורות עקיפים בלבד (ללא התאמה ישירה קודמת לסעיף), ' +
  'מה ההמלצה — לקבל את בקשת הלקוח כפי שהיא, לדחות, או להציע עריכה? הסבר בקצרה.';

export const ASK_AGENT_DEEP_RESEARCH_DECLINED_REPLY =
  'בסדר. ניתן לשאול שאלה אחרת על הארכיון או על בקשת הלקוח.';

export function clauseHasMatchedPastNegotiations(clause: CrossCheckClause): boolean {
  const options = clause.past_agreement?.options ?? [];
  return options.length > 0;
}

export function clauseHasBankOfChangesForAsk(clause: CrossCheckClause): boolean {
  return getBankOfChangesOptionsForClause(clause).length > 0;
}

/** True when the user must pick BoC vs project archive vs both before the first agent question. */
export function clauseNeedsAskAgentContextSourceChoice(clause: CrossCheckClause): boolean {
  return clauseHasBankOfChangesForAsk(clause) && clauseHasMatchedPastNegotiations(clause);
}

export function parseAskAgentContextSourceChoice(text: string): AskAgentContextSource | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (/שניהם|שתיהן|both|גם וגם|הכל|כולם/.test(t)) return 'both';
  if (/^3$/.test(t)) return 'both';
  if (/בנק\s*השינויים|בנק\s*שינויים|^בנק$|שינויים|boc|bank\s*of\s*changes/.test(t)) {
    return 'bank_of_changes';
  }
  if (/^1$/.test(t)) return 'bank_of_changes';
  if (
    /ארכיון|משא\s*ומתן|משא ומתן|ארכיון\s*הפרויקט|archive|past\s*negotiation/.test(t)
  ) {
    return 'past_negotiations';
  }
  if (/^2$/.test(t)) return 'past_negotiations';
  return null;
}

export function getAskAgentInitialQuestionForSource(
  source: AskAgentContextSource,
  domain: AskAgentDomain,
): string {
  if (source === 'bank_of_changes') {
    return (
      'בהתבסס על בנק השינויים ועל בקשת הלקוח, מה ההמלצה שלך — לקבל את הבקשה כפי שהיא, ' +
      'לדחות, או להציע עריכה? אנא הסבר בקצרה.'
    );
  }
  if (source === 'past_negotiations') {
    return getAskAgentInitialQuestion(domain);
  }
  if (domain === 'construction-loan') {
    return (
      'בהתבסס על בנק השינויים, על התאמות משא ומתן קודם לסעיף, ועל ארכיון המשא ומתן של הפרויקט — ' +
      'מה ההמלצה שלך לגבי בקשת הלקוח (לקבל, לדחות, או להציע עריכה)? אנא הסבר בקצרה.'
    );
  }
  return ASK_AGENT_INITIAL_QUESTION_MCC;
}

export function getAskAgentContextSourceAppliedReply(source: AskAgentContextSource): string {
  if (source === 'bank_of_changes') return ASK_AGENT_CONTEXT_SOURCE_APPLIED_BANK;
  if (source === 'past_negotiations') return ASK_AGENT_CONTEXT_SOURCE_APPLIED_ARCHIVE;
  return ASK_AGENT_CONTEXT_SOURCE_APPLIED_BOTH;
}

/** Same resolution as CrossCheckClauseCard `getBocOptions` — all card BoC rows. */
export function getBocOptionsFromClause(clause: CrossCheckClause): BankOfChangesOption[] {
  const boc = clause.bank_of_changes;
  if (!boc) return [];
  if (typeof boc === 'string') {
    const t = boc.trim();
    return t
      ? [{ text: t, score: 0, rank: 1, original_extracted: t }]
      : [];
  }
  if (typeof boc === 'object' && boc !== null && 'options' in boc && Array.isArray(boc.options)) {
    return boc.options as BankOfChangesOption[];
  }
  if (Array.isArray(boc)) {
    return boc as BankOfChangesOption[];
  }
  if (typeof boc === 'object' && boc !== null && 'chosen_option' in boc) {
    const co = String((boc as BankOfChangesMulti).chosen_option || '').trim();
    return co
      ? [{ text: co, score: 0, rank: 1, original_extracted: co }]
      : [];
  }
  return [];
}

export function resolveBocOptionDisplayText(opt: BankOfChangesOption): string {
  return (opt.text?.trim() || opt.original_extracted?.trim() || '').trim();
}

/** Normalized bank-of-changes options for Ask Agent context (matches card list). */
export function getBankOfChangesOptions(
  boc: BankOfChangesValue | undefined,
): Array<{
  text: string;
  score?: number;
  rank?: number;
  original_extracted?: string;
  internal_note?: string;
}> {
  if (!boc) return [];
  if (typeof boc === 'string') {
    const t = boc.trim();
    return t ? [{ text: t, rank: 1, original_extracted: t }] : [];
  }
  if ('options' in boc && Array.isArray(boc.options)) {
    return (boc.options as BankOfChangesOption[])
      .map((o, i) => ({
        text: resolveBocOptionDisplayText(o),
        score: o.score,
        rank: o.rank ?? i + 1,
        original_extracted: o.original_extracted,
        internal_note: o.internal_note,
      }))
      .filter((o) => o.text);
  }
  if (Array.isArray(boc)) {
    return (boc as BankOfChangesOption[])
      .map((o, i) => ({
        text: resolveBocOptionDisplayText(o),
        score: o.score,
        rank: o.rank ?? i + 1,
        original_extracted: o.original_extracted,
        internal_note: o.internal_note,
      }))
      .filter((o) => o.text);
  }
  if ('chosen_option' in boc && boc.chosen_option) {
    const t = String(boc.chosen_option).trim();
    return t ? [{ text: t, rank: 1, original_extracted: t }] : [];
  }
  return [];
}

export function getBankOfChangesOptionsForClause(
  clause: CrossCheckClause,
): ReturnType<typeof getBankOfChangesOptions> {
  return getBocOptionsFromClause(clause)
    .map((o, i) => ({
      text: resolveBocOptionDisplayText(o),
      score: o.score,
      rank: o.rank ?? i + 1,
      original_extracted: o.original_extracted,
      internal_note: o.internal_note,
    }))
    .filter((o) => o.text);
}

/** Whether the clause already has cross-check matches to ground the first agent question. */
export function clauseHasAskAgentGrounding(
  clause: CrossCheckClause,
  domain: AskAgentDomain,
): boolean {
  if (domain === 'construction-loan') {
    return clauseHasMatchedPastNegotiations(clause);
  }
  if ((clause.sales_agreement || '').trim()) return true;
  if (getBankOfChangesOptionsForClause(clause).length > 0) return true;
  if ((clause.insights?.length ?? 0) > 0) return true;
  return false;
}

const DEEP_RESEARCH_AFFIRMATIVE =
  /^(כן|כן[,\s]|yes|y|ok|אוקי|אשמח|בצע|חפש|חפשי|בבקשה|please|go ahead)/i;
const DEEP_RESEARCH_NEGATIVE = /^(לא|לא\.|no|n|לא תודה|תודה)/i;

export function isDeepResearchAffirmative(text: string): boolean {
  const t = text.trim();
  return DEEP_RESEARCH_AFFIRMATIVE.test(t);
}

export function isDeepResearchDecline(text: string): boolean {
  const t = text.trim();
  return DEEP_RESEARCH_NEGATIVE.test(t);
}

/** True when the agent answer recommends suggesting an edit to the client request. */
export function isSuggestEditRecommendation(text: string): boolean {
  const t = text.trim();
  return (
    /המלצה\s*:\s*להציע\s*עריכה/i.test(t) ||
    /להציע\s*עריכה/i.test(t) ||
    /הציע\s*עריכה/i.test(t) ||
    /suggest\s*edit/i.test(t)
  );
}

export const ASK_AGENT_SUGGEST_EDIT_OFFER =
  'ההמלצה היא להציע עריכה. האם תרצה שאכין נוסח מוצע **צר** לבקשת הלקוח — רק לפי מה שהלקוח מבקש, ולא שינויים רחבים יותר מהמקורות?';

export const ASK_AGENT_SUGGEST_EDIT_DECLINED_REPLY =
  'בסדר. ניתן להמשיך לשאול שאלות או להפעיל + AI על הכרטיס ידנית.';

export const ASK_AGENT_SUGGEST_EDIT_APPLIED_REPLY =
  'העריכה המוצעת הוחלה על בקשת הלקוח בכרטיס הסעיף. ניתן לעיין ולשלוח לתגובה.';

export type SuggestEditSourceType = 'bank_of_changes' | 'past_agreement' | 'both';

/** Maps Ask Agent context choice to engine `source_type` for suggestion stream. */
export function suggestEditSourceTypeFromContext(
  contextSource: AskAgentContextSource | null | undefined,
): SuggestEditSourceType {
  if (contextSource === 'bank_of_changes') return 'bank_of_changes';
  if (contextSource === 'past_negotiations') return 'past_agreement';
  return 'both';
}

/**
 * Reference for suggest-edit: clause sources (BoC / past matches) + agent rationale,
 * scoped so the model edits only what the client requested — not every change in the sources.
 */
export function buildSuggestEditReferenceForClause(
  clause: CrossCheckClause,
  messages: Array<{ role: string; content: string }>,
  contextSource: AskAgentContextSource | null | undefined,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string> = {},
): { referenceText: string; referenceExplanation: string; sourceType: SuggestEditSourceType } {
  const lastAssistant =
    [...messages].reverse().find((m) => m.role === 'assistant')?.content?.trim() ?? '';
  const interpretation = getInterpretationForAsk(clause);
  const clientPlain = getClientRequestPlainTextForAsk(clause);

  const includeBoc =
    !contextSource || contextSource === 'bank_of_changes' || contextSource === 'both';
  const includePast =
    !contextSource || contextSource === 'past_negotiations' || contextSource === 'both';

  const parts: string[] = [
    '=== SCOPE: MINIMAL EDIT TO CLIENT REQUEST ONLY ===',
    'The reference blocks below may contain full suggested responses or long past-negotiation texts.',
    'Use ONLY the fragments that directly address what the client is asking for in this paragraph.',
    'Ignore any wider, additional, or unrelated changes described in those sources.',
    '',
    '--- What the client is asking for (client request — accepted text) ---',
    clientPlain || '(none)',
    '',
    '--- Interpretation of track changes (what changed / what the client wants) ---',
    interpretation || '(none)',
  ];

  if (includeBoc) {
    const bocOpts = getBankOfChangesOptionsForClause(clause);
    if (bocOpts.length) {
      parts.push(
        '',
        '--- Bank of Changes options (use only phrases relevant to the client ask above) ---',
      );
      bocOpts.forEach((opt, i) => {
        parts.push(...formatBankOfChangesBlock(opt, i + 1));
      });
    } else {
      parts.push('', '--- Bank of Changes: (none on this clause) ---');
    }
  }

  if (includePast) {
    const pastOpts = clause.past_agreement?.options ?? [];
    if (pastOpts.length) {
      parts.push(
        '',
        '--- Matched past negotiations (use only phrases relevant to the client ask above) ---',
      );
      pastOpts.slice(0, 5).forEach((opt, i) => {
        parts.push(
          ...formatPastNegotiationBlock(opt, i + 1, playbookPlainTextById, playbookFileNameById),
        );
      });
    } else {
      parts.push('', '--- Matched past negotiations: (none on this clause) ---');
    }
  }

  const sourceType = suggestEditSourceTypeFromContext(contextSource);

  const explanationParts = [
    'עריכה מוצעת צרה לפסקת בקשת הלקוח בלבד.',
    'החל שינוי רק כדי לענות על מה שהלקוח מבקש — לא את כל מה שמופיע במקורות.',
    '',
    'המלצת הסוכן (רationale):',
    lastAssistant || '(אין)',
  ];

  return {
    referenceText: parts.join('\n').slice(0, 30_000),
    referenceExplanation: explanationParts.join('\n'),
    sourceType,
  };
}

/** @deprecated Prefer buildSuggestEditReferenceForClause for Ask Agent suggest-edit. */
export function buildChatReferenceForSuggestion(
  messages: Array<{ role: string; content: string }>,
): { referenceText: string; referenceExplanation: string } {
  const lines = messages.map((m) => {
    const label = m.role === 'user' ? 'משתמש' : 'סוכן';
    return `${label}: ${m.content}`;
  });
  const referenceText = lines.join('\n\n').slice(0, 30_000);
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content ?? '';
  return {
    referenceText,
    referenceExplanation: `המלצת הסוכן והקשר השיחה:\n${lastAssistant}`.trim(),
  };
}

export function getClientRequestXml(clause: CrossCheckClause): string | null {
  const cr = clause.client_request?.trim() || '';
  if (
    cr &&
    (cr.startsWith('<w:') ||
      cr.startsWith('<pkg:') ||
      cr.startsWith('<?xml') ||
      cr.includes('<w:p') ||
      cr.includes('<p '))
  ) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

export function getClientRequestPlainTextForAsk(clause: CrossCheckClause): string {
  const xml = getClientRequestXml(clause);
  if (xml) {
    try {
      return TrackChangesParser.extractAcceptedText(xml).replace(/\s+/g, ' ').trim();
    } catch {
      /* fallthrough */
    }
  }
  if (clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      return (parsed.edited_text || parsed.original_text || clause.client_request).trim();
    } catch {
      return clause.client_request.trim();
    }
  }
  return (clause.client_request || '').trim();
}

export function getInterpretationForAsk(clause: CrossCheckClause): string {
  const fromClause = clause.track_changes_interpretation?.trim();
  if (fromClause) return fromClause;
  const fromInsight = clause.insights?.[0]?.explanation?.trim();
  return fromInsight || '';
}

export function buildClauseAskAgentPrefix(
  clause: CrossCheckClause,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string> = {},
  domain: AskAgentDomain = 'construction-loan',
  contextSource?: AskAgentContextSource | null,
): string {
  const includeBoc =
    contextSource == null ||
    contextSource === 'bank_of_changes' ||
    contextSource === 'both';
  const includePast =
    contextSource == null ||
    contextSource === 'past_negotiations' ||
    contextSource === 'both';
  /** While awaiting user source choice, omit BoC and past match blocks from context. */
  const pendingSourceChoice = contextSource == null;
  const effectiveIncludeBoc = pendingSourceChoice ? false : includeBoc;
  const effectiveIncludePast = pendingSourceChoice ? false : includePast;
  const includeSalesAgreement =
    domain === 'real-estate' && contextSource !== 'bank_of_changes';
  const includeInsights =
    domain === 'real-estate' &&
    contextSource !== 'bank_of_changes' &&
    contextSource !== 'past_negotiations';
  const clauseLabel =
    clause.clause_number != null
      ? formatClauseNumberForDisplay(clause.clause_number)
      : '—';
  const clientRequest = getClientRequestPlainTextForAsk(clause);
  const interpretation = getInterpretationForAsk(clause);

  const lines: string[] = [
    '=== CURRENT CLAUSE (open in Word) ===',
    `Clause: ${clauseLabel}`,
    '',
    '--- Client request ---',
    clientRequest || '(none)',
    '',
    '--- Interpretation (track changes) ---',
    interpretation || '(none)',
  ];

  if (domain === 'real-estate') {
    if (includeSalesAgreement) {
      lines.push('', '--- Matched Sales Agreement clause ---');
      const sa = (clause.sales_agreement || '').trim();
      lines.push(sa || '(no sales agreement match for this clause)');
    }

    if (effectiveIncludeBoc) {
      lines.push('', '--- Bank of Changes suggestions for this clause ---');
      const bocOpts = getBankOfChangesOptionsForClause(clause);
      if (!bocOpts.length) {
        lines.push('(no bank of changes suggestions for this clause)');
      } else {
        bocOpts.forEach((opt, i) => {
          lines.push(...formatBankOfChangesBlock(opt, i + 1));
        });
      }
    }

    if (includeInsights) {
      lines.push('', '--- Existing clauses that may already satisfy the request ---');
      const insights = clause.insights ?? [];
      if (!insights.length) {
        lines.push('(no existing-clause insights for this clause)');
      } else {
        insights.slice(0, 5).forEach((ins, i) => {
          lines.push(...formatInsightBlock(ins, i + 1));
        });
      }
    }

    if (effectiveIncludePast) {
      lines.push('', '--- Matched past negotiations for this clause ---');
      const pastOpts = clause.past_agreement?.options ?? [];
      if (!pastOpts.length) {
        lines.push('(no past negotiation matches for this clause)');
      } else {
        pastOpts.slice(0, 5).forEach((opt, i) => {
          lines.push(...formatPastNegotiationBlock(opt, i + 1, playbookPlainTextById, playbookFileNameById));
        });
      }
    }
  } else {
    if (effectiveIncludePast) {
      lines.push('', '--- Matched past negotiations for this clause ---');
      const options = clause.past_agreement?.options ?? [];
      if (!options.length) {
        lines.push('(no past negotiation matches for this clause)');
      } else {
        options.slice(0, 5).forEach((opt, i) => {
          lines.push(...formatPastNegotiationBlock(opt, i + 1, playbookPlainTextById, playbookFileNameById));
        });
      }
    }
    if (effectiveIncludeBoc) {
      lines.push('', '--- Bank of Changes suggestions for this clause ---');
      const bocOpts = getBankOfChangesOptionsForClause(clause);
      if (!bocOpts.length) {
        lines.push('(no bank of changes suggestions for this clause)');
      } else {
        bocOpts.forEach((opt, i) => {
          lines.push(...formatBankOfChangesBlock(opt, i + 1));
        });
      }
    }
  }

  return lines.join('\n');
}

function formatBankOfChangesBlock(
  opt: {
    text: string;
    score?: number;
    rank?: number;
    original_extracted?: string;
    internal_note?: string;
  },
  rank: number,
): string[] {
  const score =
    opt.score != null && !Number.isNaN(Number(opt.score))
      ? `Relevance score: ${opt.score}\n`
      : '';
  const note = opt.internal_note?.trim()
    ? `Internal note: ${opt.internal_note.trim()}\n`
    : null;
  const extracted =
    opt.original_extracted?.trim() &&
    opt.original_extracted.trim() !== opt.text.trim()
      ? `Original extracted text: ${opt.original_extracted.trim()}\n`
      : null;
  return [
    `--- Bank of Changes option ${rank} ---`,
    score ? score.trimEnd() : null,
    note,
    extracted,
    'Suggested response text:',
    opt.text.trim() || '(text unavailable)',
    '',
  ].filter((l) => l !== null) as string[];
}

function formatInsightBlock(ins: CrossCheckInsight, rank: number): string[] {
  const exist = ins.exist_clause?.trim() || '(clause text unavailable)';
  const explanation = ins.explanation?.trim() || '(none)';
  const score =
    ins.similarity_score != null && !Number.isNaN(Number(ins.similarity_score))
      ? `Similarity score: ${ins.similarity_score}\n`
      : '';
  return [
    `--- Existing clause insight ${rank} ---`,
    score ? score.trimEnd() : null,
    'Existing clause text:',
    exist,
    '',
    'Explanation (why this clause may satisfy the client request):',
    explanation,
    '',
  ].filter((l) => l !== null) as string[];
}

function formatPastNegotiationBlock(
  opt: PastAgreementOption,
  rank: number,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string>,
): string[] {
  const text =
    (opt.id && playbookPlainTextById[opt.id]?.trim()) ||
    opt.text?.trim() ||
    '(text unavailable)';
  const source =
    (opt.id && playbookFileNameById[opt.id]?.trim()) || opt.id || '(unknown source)';
  const explanation = opt.explanation?.trim() || '(none)';
  const similarity =
    opt.similarity != null && !Number.isNaN(Number(opt.similarity))
      ? `Similarity score: ${opt.similarity}\n`
      : '';

  return [
    `--- Match ${rank} ---`,
    `Source: ${source}`,
    similarity ? similarity.trimEnd() : null,
    'Past negotiation text:',
    text,
    '',
    'Explanation (how this match relates to the client request):',
    explanation,
    '',
  ].filter((l) => l !== null) as string[];
}

/** Prepends mode instructions so the model respects BoC vs archive vs both. */
export function applyAskAgentContextSourceBanner(
  body: string,
  source: AskAgentContextSource | null | undefined,
): string {
  const trimmed = body.trim();
  if (!trimmed || !source) return trimmed;
  if (source === 'bank_of_changes') {
    return (
      '=== ASK AGENT MODE: BANK OF CHANGES ONLY ===\n' +
      'Use ONLY the "Bank of Changes suggestions" section in this context. ' +
      'Reference options by their "Bank of Changes option N" labels. ' +
      'Do NOT cite project playbook archive records (Record 1, Record 2, …) or matched past negotiations.\n\n' +
      trimmed
    );
  }
  if (source === 'past_negotiations') {
    return (
      '=== ASK AGENT MODE: PROJECT NEGOTIATION ARCHIVE ===\n' +
      'Use matched past negotiations for this clause and the PROJECT PLAYBOOK archive below. ' +
      'Do NOT use Bank of Changes suggestions for this session.\n\n' +
      trimmed
    );
  }
  return (
    '=== ASK AGENT MODE: BANK OF CHANGES + NEGOTIATION ARCHIVE ===\n' +
    'Use Bank of Changes options, matched past negotiations, and the project playbook archive as needed.\n\n' +
    trimmed
  );
}

/** Clause focus first, then full project playbook; respects max context size. */
export function combineAskAgentContext(
  clausePrefix: string,
  playbookBody: string,
  options?: { includePlaybookArchive?: boolean; contextSource?: AskAgentContextSource | null },
): string {
  const includePlaybookArchive = options?.includePlaybookArchive !== false;
  const separator = '\n\n=== PROJECT PLAYBOOK (past negotiations archive) ===\n\n';
  const playbookPart = includePlaybookArchive ? (playbookBody || '').trim() : '';
  let out = applyAskAgentContextSourceBanner(clausePrefix.trim(), options?.contextSource);
  if (playbookPart) {
    out += separator + playbookPart;
  }
  if (out.length <= PLAYBOOK_CONTEXT_MAX_CHARS) {
    return out;
  }

  const note = '\n\n[Truncated: context exceeded size limit]';
  const prefixLen = clausePrefix.trim().length;
  const sepLen = playbookPart ? separator.length : 0;
  const budgetForPlaybook = PLAYBOOK_CONTEXT_MAX_CHARS - prefixLen - sepLen - note.length;

  if (budgetForPlaybook > 200 && playbookPart) {
    return (
      clausePrefix.trim() +
      separator +
      playbookPart.slice(0, budgetForPlaybook) +
      note
    );
  }

  return clausePrefix.trim().slice(0, PLAYBOOK_CONTEXT_MAX_CHARS - note.length) + note;
}
